import { useState } from 'react'
import { inputAsText, trimmed, TRIM } from '@/kinds/agentrun/transcript'
import { cx } from '@/components/ui'

// One tool call opened up, for every thread that shows tools (a coding agent's, Prime's
// own, the quick chat's): what it was given, what it answered, whether it failed, when
// and how long it took, and the thinking that led to it. Mono, capped in height, so
// opening one never pushes the thread about more than its own box.

export interface CallView {
  name: string
  input?: unknown
  result?: string
  error?: boolean
  /** ISO time of the call */
  at?: string
  ms?: number
  thinking?: string
}

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

const took = (ms: number) => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`)

/** A block of code or output: capped height, scrolls, long text trimmed with a way to the rest. */
const Mono = ({ label, text, tone }: { label: string; text: string; tone?: 'error' }) => {
  const [all, setAll] = useState(false)
  const { shown, left } = trimmed(text, TRIM)
  return (
    <div className="min-w-0">
      <span className={cx('block text-[11px] mb-0.5', tone === 'error' ? 'text-rose-300' : 'text-faint')}>
        {label}
      </span>
      <pre
        className={cx(
          'm-0 max-h-56 overflow-auto rounded-[var(--radius-card-inner)] border px-2.5 py-2 font-mono text-[11.5px] leading-snug whitespace-pre-wrap break-words',
          tone === 'error'
            ? 'border-rose-400/40 bg-rose-400/5 text-rose-200'
            : 'border-line bg-panel/60 text-fg-2',
        )}
        tabIndex={0}
      >
        {all ? text : shown}
      </pre>
      {left > 0 && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-0.5 min-h-8 text-[11.5px] text-accent-text"
        >
          {all ? 'Show less' : `Show all (${left.toLocaleString()} more characters)`}
        </button>
      )}
    </div>
  )
}

export const ToolCallDetail = ({
  call,
  pending,
  note,
  noResult = 'No result recorded.',
}: {
  call?: CallView
  /** the call is still running, so no result yet */
  pending?: boolean
  /** why there is no detail, when there is none */
  note?: string
  /** what to say where the result would be, when it is known not to be here */
  noResult?: string
}) => {
  if (!call)
    return (
      <p className="text-[11.5px] text-faint" data-testid="tool-detail">
        {note ?? 'No more detail for this one.'}
      </p>
    )
  const input = call.input === undefined ? '' : inputAsText(call.input)
  return (
    <div className="flex flex-col gap-2" data-testid="tool-detail" data-error={call.error ? '' : undefined}>
      <p className="flex flex-wrap items-center gap-x-2 text-[11.5px] text-faint">
        <span className="font-mono text-muted">{call.name}</span>
        {call.at && <span className="tabular-nums">{clock(call.at)}</span>}
        {call.ms !== undefined && <span className="tabular-nums">took {took(call.ms)}</span>}
        {call.error && (
          <span
            className="rounded-full border border-rose-400/50 px-1.5 text-rose-300"
            data-testid="tool-error"
          >
            Failed
          </span>
        )}
      </p>
      {call.thinking && <Thinking text={call.thinking} />}
      {input && <Mono label="Input" text={input} />}
      {call.result !== undefined ? (
        <Mono
          label={call.error ? 'Error' : 'Result'}
          text={call.result || '(empty)'}
          tone={call.error ? 'error' : undefined}
        />
      ) : (
        <p className="text-[11.5px] text-faint">{pending ? 'Still running…' : noResult}</p>
      )}
    </div>
  )
}

/** What the model thought, folded: prose, so not mono. */
export const Thinking = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false)
  return (
    <div data-testid="tool-thinking">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="min-h-8 text-[11.5px] text-muted hover:text-fg"
      >
        {open ? 'Hide thinking' : 'Thinking'}
      </button>
      {open && (
        <p className="m-0 max-h-56 overflow-auto whitespace-pre-wrap text-[12.5px] leading-snug text-muted italic">
          {text}
        </p>
      )}
    </div>
  )
}
